import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ChatPanel from './components/ChatPanel';
import Login from './components/Login';
import Admin from './pages/Admin';
import { MessageCircle } from 'lucide-react';
import apiService from './services/api';

function App() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('chatbot_panel_width');
    return saved ? parseInt(saved, 10) : 380;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const panelRef = useRef(null);

  // iframe mode detection
  const isEmbedded = window.self !== window.top;

  // Handle external auth via postMessage
  const handleExternalAuth = useCallback(async (data) => {
    try {
      const response = await apiService.externalLogin(data);
      if (response && response.user) {
        setCurrentUser(response.user);
        // Notify parent window
        window.parent.postMessage({ type: 'CHATBOT_AUTH_SUCCESS', user: response.user }, '*');
      }
    } catch (err) {
      console.error('External auth failed:', err);
      window.parent.postMessage({ type: 'CHATBOT_AUTH_ERROR', error: err.message }, '*');
    }
  }, []);

  // Listen for postMessage from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      const { type, data } = event.data || {};
      
      if (type === 'CHATBOT_AUTH') {
        // External system sending auth data
        handleExternalAuth(data);
      } else if (type === 'CHATBOT_LOGOUT') {
        apiService.logout();
        setCurrentUser(null);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Notify parent that ChatBot is ready
    if (isEmbedded) {
      window.parent.postMessage({ type: 'CHATBOT_READY' }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbedded, handleExternalAuth]);

  // Check for existing session
  useEffect(() => {
    const token = apiService.getToken();
    if (token) {
      apiService.getCurrentUser()
        .then(user => setCurrentUser(user))
        .catch(() => apiService.logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Animated open/close
  const openPanel = useCallback(() => {
    setIsVisible(true);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 350);
  }, []);

  // Resize handlers
  useEffect(() => {
    if (!isResizing) return;

    const handleResizeMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(320, Math.min(800, newWidth));
      setPanelWidth(clamped);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      localStorage.setItem('chatbot_panel_width', String(panelWidth));
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, panelWidth]);

  // Click outside to minimize
  useEffect(() => {
    if (!isOpen || isResizing) return;

    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const isFloatingButton = e.target.closest('[data-chatbot-toggle]');
        if (!isFloatingButton) {
          closePanel();
        }
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 400);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isResizing, closePanel]);

  const handleLogin = (user, token) => {
    setCurrentUser(user);
  };

  const handleLogout = () => {
    apiService.logout();
    setCurrentUser(null);
  };

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: 'white'
      }}>
        Yükleniyor...
      </div>
    );
  }

  // Embedded mode - full panel
  if (isEmbedded) {
    if (!currentUser) {
      return <Login onLogin={handleLogin} />;
    }
    
    if (showAdmin && currentUser?.isAdmin) {
      return <Admin onBack={() => setShowAdmin(false)} currentUser={currentUser} />;
    }
    
    return (
      <ChatPanel 
        isEmbedded={true} 
        onClose={() => {}} 
        currentUser={currentUser} 
        onLogout={handleLogout}
        onOpenAdmin={currentUser?.isAdmin ? () => setShowAdmin(true) : undefined}
        onUnreadCountChange={(count) => setUnreadCount(count)}
      />
    );
  }

  // Standalone mode
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        textAlign: 'center',
        color: 'white'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Klinik Chat Demo</h1>
        <p style={{ opacity: 0.8, marginBottom: '2rem' }}>
          {currentUser 
            ? `Hoş geldiniz, ${currentUser.fullName}` 
            : 'Sağ alttaki butona tıklayarak chat panelini açabilirsiniz'}
        </p>
      </div>

      {/* Floating Button */}
      {!isOpen && (
        <button
          data-chatbot-toggle="true"
          onClick={openPanel}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.5)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 25px rgba(59, 130, 246, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.5)';
          }}
        >
          <MessageCircle size={28} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              minWidth: '18px',
              height: '18px',
              borderRadius: '50%',
              background: '#ef4444',
              color: 'white',
              fontSize: '11px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px'
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      <div
          ref={panelRef}
          style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: `${panelWidth}px`,
          maxWidth: '100vw',
          zIndex: 1001,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: isResizing ? 'none' : 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          display: isVisible ? 'flex' : 'none',
          boxShadow: isOpen ? '-4px 0 25px rgba(0,0,0,0.15)' : 'none'
        }}>
          <style>
            {`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
          {/* Resize Handle */}
          <div
            onMouseDown={handleResizeStart}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '5px',
              cursor: 'col-resize',
              zIndex: 1002,
              background: isResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
            }}
            onMouseEnter={(e) => { if (!isResizing) e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)'; }}
            onMouseLeave={(e) => { if (!isResizing) e.currentTarget.style.background = 'transparent'; }}
          />
          <div style={{ flex: 1, width: '100%', overflow: 'hidden' }}>
            {currentUser ? (
              showAdmin ? (
                <Admin
                  onBack={() => setShowAdmin(false)}
                  currentUser={currentUser}
                />
              ) : (
                <ChatPanel
                  isEmbedded={false}
                  onClose={handleLogout}
                  onMinimize={closePanel}
                  currentUser={currentUser}
                  onLogout={handleLogout}
                  onOpenAdmin={() => setShowAdmin(true)}
                  onUnreadCountChange={(count) => setUnreadCount(count)}
                />
              )
            ) : (
              <Login onLogin={handleLogin} />
            )}
          </div>
        </div>
    </div>
  );
}

export default App;
